import { formatDate } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EdgeConnectionRecord } from '@models/edge/edge-connection-record';
import {
  EdgeTransactionDirection,
  EdgeTransactionRecord,
  EdgeTransactionStatus
} from '@models/edge/edge-transaction-record';
import { CellDataType } from '@models/grid/cell-data-type.enum';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BaseListGridComponent } from "@shared/components/grid/components/base-list-grid/base-list-grid.component";
import {
  GridServerSideComponent
} from "@shared/components/grid/components/grid-server-side/grid-server-side.component";
import { PageTitleComponent } from "@shared/components/page-title/page-title.component";
import { MenuService } from '@shared/services/menu.service';
import { convertEnumToFilterOption } from '@shared/utils/enum-to-filter-option.helper';
import { dateComparator } from '@shared/utils/grid/date-comparator';
import { CellClickedEvent, ColDef } from 'ag-grid-community';
import {
  GetRowIdFunc,
  GetRowIdParams,
  GridApi,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ValueFormatterParams
} from 'ag-grid-enterprise';
import { ToastrService } from 'ngx-toastr';
import { SuperuserApiService } from '../../services/superuser-api.service';
import {
  EdgeTransactionDetailsComponent
} from './components/edge-transaction-details/edge-transaction-details.component';
import {
  PublishOrganisationSignalComponent
} from './components/publish-organisation-signal-modal/publish-organisation-signal-modal.component';

@Component({
  selector: 'app-bulk-changes',
  standalone: true,
  imports: [
    EdgeTransactionDetailsComponent,
    PageTitleComponent,
    GridServerSideComponent
  ],
  providers: [SuperuserApiService],
  templateUrl: 'edge-transactions.component-page.html',
  styleUrl: 'edge-transactions.component-page.scss'
})
export default class EdgeTransactionsComponentPage implements OnInit {
  superuserService = inject(SuperuserApiService);
  modalService = inject(NgbModal);
  menuService = inject(MenuService);
  toast = inject(ToastrService);
  destroyRef = inject(DestroyRef);

  organisations = new Map<string, string>();

  loading = signal(true);
  $selectedTransactionRecord = signal<EdgeTransactionRecord | null>(null);

  ngOnInit(): void {
    this.menuService.setMenuState(true);
    this.superuserService.listOrganisationNames()
      .subscribe({
        next: (result) => {
          this.organisations = result;
          this.loading.set(false);
        },
        error: (err) => {
          console.error(err);
          this.toast.error('Error while loading organisations', 'Error');
        }
      });
  }

  dataSource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => {
      const request = params.request;
      this.superuserService.getEdgeTransactions(request).subscribe({
        next: (result) => {
          params.success({
            rowData: result.records,
            rowCount: result.total,
          });
        },
        error: (err) => {
          console.error(err);
          params.fail();
        },
      });
    },
  };

  connectionsDataSource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => {
      const request = params.request;
      this.superuserService.getEdgeConnections(request).subscribe({
        next: (result) => {
          params.success({
            rowData: result.records,
            rowCount: result.total,
          });
        },
        error: (err) => {
          console.error(err);
          params.fail();
        },
      });
    },
  };

  //sync CreatedOn/OrgId filters
  //working but confusing
  onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;
    // gridApi.addEventListener('filterChanged', (event : FilterChangedEvent) => {
    //   const allFilters = event.api.getFilterModel();
    //   const filterState = {
    //       // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    //       createdOn: allFilters['createdOn'],
    //       // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    //     };

    //   this.connectionsGridApi.setFilterModel(filterState);
    // });
  }

  onConnectionsGridReady(gridApi: GridApi): void {
    this.connectionsGridApi = gridApi;
  }

  cellClickHandler(params: CellClickedEvent<EdgeTransactionRecord>): void {
    if (params.data) {
      this.$selectedTransactionRecord.set(params.data);
    }
  }

  onCloseDetails(): void {
    this.$selectedTransactionRecord.set(null);
  }

  refreshTransactionsGrid(): void {
    this.gridApi.refreshServerSide();
    this.toast.info('Transactions refreshed');
  }

  refreshConnectionsGrid(): void {
    this.connectionsGridApi.refreshServerSide();
    this.toast.info('Connections refreshed');
  }

  refreshAll(): void {
    this.refreshTransactionsGrid();
    this.refreshConnectionsGrid();
  }

  triggerOrganisationSignal(): void {
    const modalRef = this.modalService.open(PublishOrganisationSignalComponent, { size: 'sm' });
    const instance = (modalRef.componentInstance as PublishOrganisationSignalComponent);

    instance.organisations = Array.from(this.organisations, ([key, value]) => ({
      label: value,
      value: key,
      disabled: false
    }));

    instance.confirm
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }


  // ===== ===== ===== Table Columns config and other ===== ===== =====


  readonly getRowId: GetRowIdFunc = (params: GetRowIdParams<EdgeTransactionRecord>) => params.data.transactionId;
  readonly connectionsGetRowId: GetRowIdFunc = (params: GetRowIdParams<EdgeConnectionRecord>) => params.data.connectionAttemptId;
  readonly rowSelection = BaseListGridComponent.noCheckboxesRowSelection;

  colDefs = this.getEdgeTransactionsColDefs();
  connectionsColDefs = this.getConnectionsColDefs();
  gridApi!: GridApi;
  connectionsGridApi!: GridApi;

  getEdgeTransactionsColDefs(): ColDef[] {
    return [
      {
        headerName: 'Created On',
        field: 'createdOn',
        width: 200,
        cellDataType: CellDataType.DATE,
        valueFormatter: (params: ValueFormatterParams<EdgeTransactionRecord, Date>) => {
          return params.value ? formatDate(params.value, 'dd/MM/yyyy HH:mm:ss.SSS', 'en-US') : '';
        },
        sort: "desc",
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: dateComparator,
        },
        menuTabs: [],
        onCellClicked: this.cellClickHandler.bind(this)
      },
      {
        headerName: 'Transmitted On',
        field: 'transmittedOn',
        width: 200,
        cellDataType: CellDataType.DATE,
        valueFormatter: (params: ValueFormatterParams<EdgeTransactionRecord, Date>) => {
          return params.value ? formatDate(params.value, 'dd/MM/yyyy HH:mm:ss.SSS', 'en-US') : '';
        },
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: dateComparator,
        },
        menuTabs: [],
        onCellClicked: this.cellClickHandler.bind(this)
      },
      {
        headerName: 'Form Code',
        field: 'formCode',
        flex: 1,
        filter: "agTextColumnFilter",
        cellDataType: CellDataType.TEXT,
        menuTabs: [],
        filterParams: {},
        onCellClicked: this.cellClickHandler.bind(this)
      },
      {
        headerName: 'Direction',
        field: 'direction',
        width: 100,
        valueFormatter: (params: ValueFormatterParams<EdgeTransactionRecord, EdgeTransactionDirection>) => {
          return params.value == EdgeTransactionDirection.Inbound ? 'Inbound' : 'Outbound';
        },
        filter: "agSetColumnFilter",
        filterParams: {
          filterOptions: convertEnumToFilterOption(EdgeTransactionDirection),
          values: Object.values(EdgeTransactionDirection).filter((v) => !isNaN(Number(v))),
        },
        menuTabs: [],
        onCellClicked: this.cellClickHandler.bind(this)
      },
      {
        headerName: 'Status',
        field: 'status',
        flex: 1,
        valueFormatter: (params: ValueFormatterParams<EdgeTransactionRecord, EdgeTransactionStatus>) => {
          return EdgeTransactionStatus[params.value!];
        },
        filter: "agSetColumnFilter",
        filterParams: {
          filterOptions: convertEnumToFilterOption(EdgeTransactionStatus),
          values: Object.values(EdgeTransactionStatus).filter((v) => !isNaN(Number(v))),
        },
        menuTabs: [],
        onCellClicked: this.cellClickHandler.bind(this)
      },
      {
        headerName: 'Organisation',
        field: 'organisationId',
        flex: 1,
        valueFormatter: (params: ValueFormatterParams<EdgeTransactionRecord, string>) => {
          return this.organisations.get(params.value!) ?? '';
        },
        filter: "agSetColumnFilter",
        filterParams: {
          filterOptions: Array.from(this.organisations, ([key, value]) => ({
            label: value,
            value: key,
            active: false
          })).sort((a, b) => a.label.localeCompare(b.label)),
          values: Array.from(this.organisations.keys()),
        },
        menuTabs: [],
        onCellClicked: this.cellClickHandler.bind(this)
      },
      {
        headerName: 'Trace Number',
        field: 'traceNumber',
        width: 160,
        filter: "agTextColumnFilter",
        cellDataType: CellDataType.TEXT,
        menuTabs: [],
        filterParams: {},
        onCellClicked: this.cellClickHandler.bind(this)
      },
      {
        headerName: 'Mailbox',
        field: 'useSecondary',
        width: 110,
        valueFormatter: (params: ValueFormatterParams<EdgeTransactionRecord, boolean>) => {
          return params.value ? 'Secondary' : 'Primary';
        },
        filter: "agSetColumnFilter",
        filterParams: {
          filterOptions: [{ label: 'Primary', value: 'false', active: false }, {
            label: 'Secondary',
            value: 'true',
            active: false
          }],
          values: ['true', 'false'],
        },
        menuTabs: [],
        onCellClicked: this.cellClickHandler.bind(this)
      },
      {
        headerName: 'Documents',
        field: 'documentIdsString',
        flex: 1,
        filter: "agTextColumnFilter",
        cellDataType: CellDataType.TEXT,
        menuTabs: [],
        filterParams: {},
        onCellClicked: this.cellClickHandler.bind(this),
        hide: true
      },
      {
        headerName: 'Transaction Text',
        field: 'transactionText',
        flex: 1,
        filter: "agTextColumnFilter",
        cellDataType: CellDataType.TEXT,
        menuTabs: [],
        filterParams: {},
        onCellClicked: this.cellClickHandler.bind(this),
        hide: true
      }
    ];
  }

  getConnectionsColDefs(): ColDef[] {
    return [
      {
        headerName: 'Created On',
        field: 'createdOn',
        width: 200,
        cellDataType: CellDataType.DATE,
        valueFormatter: (params: ValueFormatterParams<EdgeConnectionRecord, Date>) => {
          return params.value ? formatDate(params.value, 'dd/MM/yyyy HH:mm:ss.SSS', 'en-US') : '';
        },
        sort: "desc",
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: dateComparator,
        },
        menuTabs: []
      },
      {
        headerName: 'Organisation',
        field: 'organisationId',
        flex: 1,
        valueFormatter: (params: ValueFormatterParams<EdgeConnectionRecord, string>) => {
          return this.organisations.get(params.value!) ?? '';
        },
        filter: "agSetColumnFilter",
        filterParams: {
          filterOptions: Array.from(this.organisations, ([key, value]) => ({
            label: value,
            value: key,
            active: false
          })).sort((a, b) => a.label.localeCompare(b.label)),
          values: Array.from(this.organisations.keys()),
        },
        menuTabs: []
      },
      {
        headerName: 'Response Code',
        field: 'responseCode',
        width: 80,
        filter: "agTextColumnFilter",
        cellDataType: CellDataType.TEXT,
        menuTabs: [],
        filterParams: {}
      },
      {
        headerName: 'Mailbox',
        field: 'secondaryMailbox',
        width: 85,
        valueFormatter: (params: ValueFormatterParams<EdgeConnectionRecord, boolean>) => {
          return params.value ? 'Secondary' : 'Primary';
        },
        filter: "agSetColumnFilter",
        filterParams: {
          filterOptions: [{ label: 'Primary', value: 'false', active: false }, {
            label: 'Secondary',
            value: 'true',
            active: false
          }],
          values: ['true', 'false'],
        },
        menuTabs: []
      },
      {
        headerName: 'Finished',
        field: 'communicationFinished',
        width: 80,
        valueFormatter: (params: ValueFormatterParams<EdgeConnectionRecord, boolean>) => {
          return params.value ? 'Yes' : 'No';
        },
        filter: "agSetColumnFilter",
        filterParams: {
          filterOptions: [{ label: 'Yes', value: 'true', active: false }, {
            label: 'No',
            value: 'false',
            active: false
          }],
          values: ['true', 'false'],
        },
        menuTabs: []
      }
    ];
  }
}
